import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Briefcase, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTRACT_TYPES = ['CDI', 'CDD', 'Stage', 'Bénévolat', 'Consultance', "Bourse d'études"];

const REGIONS = ['RDC', 'Rwanda', 'Burundi', 'Ouganda', 'Tanzanie', 'Sous-région des Grands Lacs', 'International'];

const EMPTY_FORM = {
  title: '',
  location: '',
  contract_type: 'CDI',
  description: '',
  profile: '',
  deadline: '',
  google_form_url: '',
  region: 'RDC',
  published: true
};

export default function JobsManager() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs`, { withCredentials: true });
      setJobs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.google_form_url.startsWith('http')) {
      toast.error("Le lien Google Form doit commencer par https://");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/jobs/${editingId}`, formData, { withCredentials: true });
        toast.success('Offre mise à jour');
      } else {
        await axios.post(`${API}/admin/jobs`, formData, { withCredentials: true });
        toast.success('Offre créée');
      }
      resetForm();
      fetchJobs();
    } catch (error) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handleEdit = (job) => {
    setEditingId(job.job_id);
    setFormData({
      title: job.title,
      location: job.location,
      contract_type: job.contract_type,
      description: job.description,
      profile: job.profile,
      deadline: job.deadline ? job.deadline.split('T')[0] : '',
      google_form_url: job.google_form_url,
      region: job.region || 'RDC',
      published: job.published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, { withCredentials: true });
      toast.success('Offre supprimée');
      fetchJobs();
    } catch (error) { toast.error('Erreur'); }
  };

  const togglePublished = async (job) => {
    try {
      await axios.put(`${API}/admin/jobs/${job.job_id}`, { ...job, published: !job.published }, { withCredentials: true });
      toast.success(job.published ? 'Offre dépubliée' : 'Offre publiée');
      fetchJobs();
    } catch (e) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div data-testid="jobs-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Offres de recrutement ({jobs.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-job-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Nouvelle offre'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="job-form">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Intitulé du poste *</label>
              <input
                type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="job-title-input"
                placeholder="Ex: Chargé(e) de programme FPT"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Type de contrat *</label>
                <select
                  value={formData.contract_type}
                  onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="job-contract-select"
                >
                  {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Lieu *</label>
                <input
                  type="text" required value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="job-location-input"
                  placeholder="Ex: Bukavu, Sud-Kivu"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Date limite *</label>
                <input
                  type="date" required value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="job-deadline-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Description du poste *</label>
              <textarea
                required rows={4} value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="job-description-input"
                placeholder="Missions, responsabilités, équipe..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Profil recherché *</label>
              <textarea
                required rows={3} value={formData.profile}
                onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="job-profile-input"
                placeholder="Compétences, expérience, qualités..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">
                Lien Google Form pour postuler *
              </label>
              <input
                type="url" required value={formData.google_form_url}
                onChange={(e) => setFormData({ ...formData, google_form_url: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                placeholder="https://docs.google.com/forms/d/e/.../viewform"
                data-testid="job-form-url-input"
              />
              <p className="text-xs text-[#5C665A] mt-1">
                Créez un formulaire sur Google Forms et collez l&apos;URL publique ici. Les candidats seront redirigés vers ce lien.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="job-published" checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="h-4 w-4"
              />
              <label htmlFor="job-published" className="text-sm text-[#2E332D]">
                Publier immédiatement (visible par les visiteurs sur /recrutement)
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-job-button">
                {editingId ? 'Mettre à jour' : 'Créer l\'offre'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : (
        <div className="space-y-4">
          {jobs.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucune offre. Cliquez sur &ldquo;Nouvelle offre&rdquo; pour créer la première.</p>
          ) : jobs.map((job) => (
            <div key={job.job_id} className="card" data-testid={`admin-job-${job.job_id}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${job.published ? 'bg-[#4A6B48]/10 text-[#4A6B48]' : 'bg-gray-100 text-gray-600'}`}>
                      {job.published ? 'Publiée' : 'Brouillon'}
                    </span>
                    <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">
                      {job.contract_type}
                    </span>
                    <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                      <MapPin size={12} /> {job.location}
                    </span>
                    <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                      <Calendar size={12} /> Échéance: {new Date(job.deadline).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D] flex items-center gap-2">
                    <Briefcase size={18} className="text-[#4A6B48]" />
                    {job.title}
                  </h3>
                  <p className="text-sm text-[#5C665A] mt-2 line-clamp-2">{job.description}</p>
                  {job.google_form_url && (
                    <a
                      href={job.google_form_url} target="_blank" rel="noreferrer"
                      className="text-xs text-[#4A6B48] hover:underline inline-flex items-center gap-1 mt-2"
                    >
                      <ExternalLink size={12} /> Voir le formulaire Google
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => togglePublished(job)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={job.published ? 'Dépublier' : 'Publier'}
                  >
                    {job.published ? <EyeOff size={18} className="text-orange-600" /> : <Eye size={18} className="text-[#4A6B48]" />}
                  </button>
                  <button onClick={() => handleEdit(job)} className="p-2 hover:bg-gray-100 rounded">
                    <Edit2 size={18} className="text-[#4A6B48]" />
                  </button>
                  <button onClick={() => handleDelete(job.job_id)} className="p-2 hover:bg-gray-100 rounded">
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
